import React, { useEffect, useRef, useState } from 'react';
import { useInView } from 'framer-motion';
import { CheckIcon, RotateCcwIcon } from 'lucide-react';
import { SummonedCard } from '../ui/SummonedCard';
import { LevelBadge } from '../ui/LevelBadge';
import { ProcessingTag } from '../ui/ProcessingTag';
import { WhyPopover } from '../ui/WhyPopover';
import { SlideToConfirm } from '../ui/SlideToConfirm';
import { useCountdown } from '../../hooks/useCountdown';
import { useDb } from '../../hooks/useDb';
import { useSpecSettings } from '../../contexts/SpecSettingsContext';
import { agendaToday, nightSummary } from '../../utils/dbSelectors';
import { playSound } from '../../utils/sound';
export function InfoCardSample() {
  const events = useDb(agendaToday);
  const next = events[0];
  return <SummonedCard agent="agenda" kicker="Info" title="Vandaag" footer={<>
          <ProcessingTag where="lokaal" data-id="element-936" data-mp="quu5106z4c2e" />
          <span className="ml-auto" data-id="element-937" data-mp="qtsr8nykpbgz"><WhyPopover traceId="agenda-vandaag" data-id="element-938" data-mp="qtj0gy5pf0a8" /></span>
        </>} data-id="element-935" data-mp="vlu348rtx06h">
      <ol className="relative space-y-4" data-id="element-939" data-mp="1hfs7903j3jhd">
        <span aria-hidden className="absolute bottom-2 left-[5px] top-2 w-px bg-white/10" data-id="element-940" data-mp="1mdtnzedhf6po" />
        {events.map(row => {
        const isNext = row.id === next?.id;
        return <li key={row.id} className="relative flex gap-4 pl-6" data-id="element-941" data-mp="1co0gaoggnbqp">
              <span aria-hidden className={`absolute left-0 top-1.5 h-[11px] w-[11px] rounded-full border ${isNext ? 'border-accent bg-accent' : 'border-white/25 bg-night-900'}`} data-id="element-942" data-mp="1cm77b2idkffj" />
              <span className={`w-12 shrink-0 text-[15px] tabular-nums ${isNext ? 'font-semibold text-ink' : 'text-ink-muted'}`} data-id="element-943" data-mp="ore82h0peh48">
                {row.start}
              </span>
              <p className={`text-[15px] ${isNext ? 'font-semibold text-ink' : 'text-ink-soft'}`} data-id="element-944" data-mp="2j3udxgpl7t8k">{row.title}</p>
            </li>;
      })}
      </ol>
    </SummonedCard>;
}
export function ProposalCardSample() {
  const [sent, setSent] = useState(0);
  const {
    soundOn
  } = useSpecSettings();
  const draft = useDb(t => t.mailDrafts.find(d => d.id === 'd-bakker-reply'));
  return <SummonedCard agent="mail" kicker="Voorstel" title="Antwoord aan klant Bakker" footer={<>
          <LevelBadge level="naOk" data-id="element-946" data-mp="28ntpyhvem8iw" />
          <ProcessingTag where="cloud" data-id="element-947" data-mp="28ov5oznd7j6y" />
          <span className="ml-auto" data-id="element-948" data-mp="35d14h5xlvhdb"><WhyPopover traceId="mail-bakker" data-id="element-949" data-mp="35c9jep1mx21a" /></span>
        </>} data-id="element-945" data-mp="2cnkfyjd3wkmd">
      <p className="line-clamp-2 text-[15px] leading-relaxed text-ink-soft" data-id="element-950" data-mp="30dest0xfe96m">{draft?.body}</p>
      <div className="mt-5" data-id="element-951" data-mp="2vm6hfzcjcdqt">
        <SlideToConfirm key={sent} label="Schuif om te versturen" confirmedLabel="Verstuurd naar klant Bakker" onConfirm={() => soundOn && playSound('klaar')} data-id="element-952" data-mp="2vlexjlsaw4gd" />
      </div>
      <div className="mt-3 flex items-center gap-2" data-id="element-953" data-mp="3o5dgylhz3z16">
        <button type="button" className="h-10 whitespace-nowrap rounded-full border border-white/10 px-4 text-[14px] font-medium text-ink-soft transition-colors duration-150 hover:border-white/20 hover:text-ink" data-id="element-954" data-mp="3jbhlbpp6mo96">
          Aanpassen
        </button>
        <button type="button" className="h-10 whitespace-nowrap rounded-full px-4 text-[14px] font-medium text-ink-muted transition-colors duration-150 hover:text-ink" data-id="element-955" data-mp="1auu63l5wr0st">
          Afwijzen
        </button>
        <button type="button" onClick={() => setSent(s => s + 1)} aria-label="Demo opnieuw" className="ml-auto grid h-9 w-9 place-items-center rounded-full text-ink-muted transition-colors duration-150 hover:bg-white/10 hover:text-ink" data-id="element-956" data-mp="1jrb92144bsdh">
          <RotateCcwIcon className="h-4 w-4" data-id="element-957" data-mp="1ykoffsr0q78b" />
        </button>
      </div>
    </SummonedCard>;
}
const RING_R = 52;
const RING_C = 2 * Math.PI * RING_R;
export function CountdownCardSample() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, {
    once: true,
    amount: 0.6
  });
  const {
    remaining,
    status,
    stop,
    restart
  } = useCountdown(60000, inView);
  const {
    soundOn
  } = useSpecSettings();
  const seconds = Math.ceil(remaining / 1000);
  useEffect(() => {
    if (status === 'klaar' && soundOn) playSound('klaar');
  }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  const done = status === 'klaar';
  const stopped = status === 'gestopt';
  return <div ref={ref} className="h-full" data-id="element-958" data-mp="23ooyj37we7xf">
      <SummonedCard agent="huis" kicker={done ? 'Gedaan' : stopped ? 'Gestopt' : 'Aankondiging'} title="Verwarming werkkamer naar 17 °C terwijl je weg bent" footer={<>
            <LevelBadge level="tenzijBezwaar" data-id="element-960" data-mp="1pvzev5fudf3h" />
            <ProcessingTag where="lokaal" data-id="element-961" data-mp="1pslaxsrj4xtx" />
            <span className="ml-auto" data-id="element-962" data-mp="1kutgos3ewzmk"><WhyPopover traceId="verwarming" data-id="element-963" data-mp="1kua1hj55cpc8" /></span>
          </>} data-id="element-959" data-mp="1fvyox0ngw0qb">
        {done || stopped ? <div className="flex h-full flex-col items-start justify-center gap-4 py-4" data-id="element-964" data-mp="31c7izbax8yh9">
            <p className="flex items-center gap-3 text-[18px] text-ink" data-id="element-965" data-mp="2waqi3fsftc0o">
              {done && <span className="grid h-9 w-9 place-items-center rounded-full bg-accent/15 text-accent" data-id="element-966" data-mp="33t7ubtrp74xc">
                  <CheckIcon className="h-5 w-5" aria-hidden data-id="element-967" data-mp="38rj5c7tqblh5" />
                </span>}
              {done ? 'Werkkamer staat op 17 °C.' : 'Niets veranderd. Blijft op 20 °C.'}
            </p>
            <button type="button" onClick={restart} className="inline-flex items-center gap-2 whitespace-nowrap text-[14px] text-ink-muted transition-colors duration-150 hover:text-ink" data-id="element-968" data-mp="2yxitln3xzhzg">
              <RotateCcwIcon className="h-3.5 w-3.5" aria-hidden data-id="element-969" data-mp="3dt8fzmd1hx4p" /> Demo opnieuw
            </button>
          </div> : <div className="flex items-center gap-6" data-id="element-970" data-mp="3hsze6b4wbfj9">
            <div className="relative h-[120px] w-[120px] shrink-0" data-id="element-971" data-mp="9eqvbus6e34a">
              <svg viewBox="0 0 120 120" className="h-full w-full -rotate-90" aria-hidden data-id="element-972" data-mp="9ce2unoskacz">
                <circle cx="60" cy="60" r={RING_R} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="6" data-id="element-973" data-mp="e6ic5dlj4uh8" />
                <circle cx="60" cy="60" r={RING_R} fill="none" stroke="rgb(var(--accent))" strokeWidth="6" strokeLinecap="round" strokeDasharray={RING_C} strokeDashoffset={RING_C * (1 - remaining / 60000)} data-id="element-974" data-mp="nyuzlqgmwylw" />
              </svg>
              <span className="absolute inset-0 grid place-items-center font-display text-[36px] font-light tabular-nums text-ink" data-id="element-975" data-mp="546a8c5d8wru">
                {seconds}
              </span>
            </div>
            <div className="flex flex-1 flex-col gap-3" data-id="element-976" data-mp="3sd1hjzvg1lmn">
              <p className="text-[14px] text-ink-soft" data-id="element-977" data-mp="3nhm5ndgzxzsv">Gaat over {seconds} s door, tenzij je stopt.</p>
              <button type="button" onClick={stop} className="h-14 w-full whitespace-nowrap rounded-2xl bg-ink text-[18px] font-semibold text-night-950 transition-colors duration-150 hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent" data-id="element-978" data-mp="3ifwve01llq5u">
                Stop
              </button>
            </div>
          </div>}
      </SummonedCard>
    </div>;
}
export function DoneCardSample() {
  const build = useDb(t => nightSummary(t).find(l => l.agent === 'code'));
  return <SummonedCard agent="code" kicker="Klaar" title="Build project X gerepareerd" footer={<>
          <LevelBadge level="zelf" data-id="element-980" data-mp="2pn33kkmaa6yd" />
          <ProcessingTag where="lokaal" data-id="element-981" data-mp="2um68m529kd6c" />
          <span className="ml-auto" data-id="element-982" data-mp="2ul4m94fss49b"><WhyPopover traceId="nacht-build" data-id="element-983" data-mp="2ujthb1am8qgm" /></span>
        </>} data-id="element-979" data-mp="2korsy7o5zsf3">
      <div className="flex gap-3" data-id="element-984" data-mp="2zdei113lfyji">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-accent/15 text-accent" data-id="element-985" data-mp="2zefvtdbzuz4x">
          <CheckIcon className="h-5 w-5" aria-hidden data-id="element-986" data-mp="26wbwxqtokuno" />
        </span>
        <p className="text-[15px] leading-relaxed text-ink-soft" data-id="element-987" data-mp="26uhb8aal4yij">
          {build?.summary}. 2 tests faalden na een dependency-update; de fix staat op een branch.
        </p>
      </div>
    </SummonedCard>;
}